import React, { useState } from 'react';
import { Patient } from '../../types';
import ToothChart from './ToothChart';
import TreatmentRecords from './TreatmentRecords';
import { Bot, Activity } from 'lucide-react';

export default function ClinicalTab({ patient }: { patient: Patient }) {
  const [viewMode, setViewMode] = useState<'2d' | '3d'>('2d');

  return (
    <div className="space-y-6">
      {/* Tooth Chart */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Dental Chart</h3>
          <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('2d')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                viewMode === '2d'
                  ? 'bg-white text-green-700 shadow'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              2D View
            </button>
            <button
              onClick={() => setViewMode('3d')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                viewMode === '3d'
                  ? 'bg-white text-green-700 shadow'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              3D View
            </button>
          </div>
        </div>
        <ToothChart dentalChart={patient.dentalChart} viewMode={viewMode} />
      </div>

      {/* Treatment Records */}
      <TreatmentRecords treatmentPlans={patient.treatmentPlans} />

      {/* AI Voice-to-Charting Notes */}
      {patient.voiceToChartingNotes && patient.voiceToChartingNotes.length > 0 && (
        <div className="bg-blue-50 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center">
            <Bot className="w-5 h-5 mr-2" />
            AI Voice-to-Charting Notes
          </h3>
          <div className="space-y-3">
            {patient.voiceToChartingNotes.map((note) => (
              <div key={note.id} className="bg-white rounded-lg p-3 border border-blue-200">
                <p className="text-sm text-blue-900 mb-2">{note.transcription}</p>
                <div className="flex flex-wrap gap-2 mb-2">
                  {note.extractedFindings.map((finding, idx) => (
                    <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                      {finding}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-blue-600">
                  Date: {note.date} • Confidence: {Math.round(note.confidence * 100)}%
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Predictive Treatment Suggestions */}
      {patient.predictiveTreatmentSuggestions && patient.predictiveTreatmentSuggestions.length > 0 && (
        <div className="bg-green-50 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-green-900 mb-3 flex items-center">
            <Activity className="w-5 h-5 mr-2" />
            AI Predictive Treatment Suggestions
          </h3>
          <div className="space-y-3">
            {patient.predictiveTreatmentSuggestions.map((suggestion) => (
              <div key={suggestion.id} className="bg-white rounded-lg p-3 border border-green-200">
                <p className="font-medium text-green-900 mb-1">{suggestion.suggestion}</p>
                <p className="text-sm text-green-700 mb-2">{suggestion.reasoning}</p>
                <div className="flex items-center justify-between">
                  <span className={`px-2 py-1 text-xs rounded ${
                    suggestion.priority === 'high' ? 'bg-red-100 text-red-800' :
                    suggestion.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {suggestion.priority} priority
                  </span>
                  <span className="text-xs text-green-600">
                    Confidence: {Math.round(suggestion.confidence * 100)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
